import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon, ThumbUpOffAlt as ThumbUpOffIcon, ThumbDownOffAlt as ThumbDownOffIcon } from '@mui/icons-material';
import { Button } from '../ui/Button';

export interface EntryFeedbackValue {
  rating: 'up' | 'down';
  reason?: string;
  submittedAt?: string;
}

interface EntryFeedbackControlProps {
  currentFeedback?: EntryFeedbackValue;
  /** Rejects when the rating was not saved; the popover then stays open with an error. */
  onSubmit: (rating: 'up' | 'down', reason: string) => Promise<void>;
  disabled?: boolean;
}

export const ENTRY_FEEDBACK_ERROR = "Couldn't save feedback. Please try again.";

/**
 * The owner's thumbs up/down + note on one Timeline item, ported from
 * salesguru-frontend's FeedbackPopover: 22 × 22 thumbs, the chosen one filled
 * on its tint; clicking opens a note popover prefilled with the saved reason.
 */
const EntryFeedbackControl: React.FC<EntryFeedbackControlProps> = ({ currentFeedback, onSubmit, disabled = false }) => {
  const [pendingRating, setPendingRating] = useState<'up' | 'down' | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopoverPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  }, []);

  const closePopover = useCallback(() => {
    setPendingRating(null);
    setReason('');
    setSubmitError(null);
  }, []);

  // Close when clicking outside
  useEffect(() => {
    if (pendingRating === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pendingRating, closePopover]);

  // Reposition if the window resizes while open
  useEffect(() => {
    if (pendingRating === null) return;
    window.addEventListener('resize', calcPosition);
    return () => window.removeEventListener('resize', calcPosition);
  }, [pendingRating, calcPosition]);

  const openPopover = (rating: 'up' | 'down') => {
    if (disabled) return;
    calcPosition();
    setPendingRating(rating);
    // Prefill from the saved feedback so the rep edits rather than retypes it
    setReason(currentFeedback?.reason || '');
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!pendingRating || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(pendingRating, reason.trim());
      closePopover();
    } catch {
      setSubmitError(ENTRY_FEEDBACK_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const isUp = currentFeedback?.rating === 'up';
  const isDown = currentFeedback?.rating === 'down';
  const thumbClass = 'inline-flex h-[22px] w-[22px] items-center justify-center rounded-inner transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <>
      <div ref={triggerRef} className="inline-flex items-center gap-1" data-testid="entry-feedback-control">
        <button
          type="button"
          onClick={() => openPopover('up')}
          disabled={disabled}
          title="Helpful"
          aria-label="Helpful"
          aria-pressed={isUp}
          className={`${thumbClass} ${isUp ? 'bg-success-tint text-success' : 'text-ink-muted hover:text-success'}`}
        >
          {isUp ? <ThumbUpIcon className="h-3 w-3" /> : <ThumbUpOffIcon className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={() => openPopover('down')}
          disabled={disabled}
          title="Not helpful"
          aria-label="Not helpful"
          aria-pressed={isDown}
          className={`${thumbClass} ${isDown ? 'bg-error-tint text-error' : 'text-ink-muted hover:text-error'}`}
        >
          {isDown ? <ThumbDownIcon className="h-3 w-3" /> : <ThumbDownOffIcon className="h-3 w-3" />}
        </button>
      </div>

      {pendingRating !== null && popoverPos && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Item feedback"
          style={{
            position: 'fixed',
            top: popoverPos.top,
            right: popoverPos.right,
            zIndex: 1300,
            width: 256,
          }}
          className="bg-surface border border-hairline rounded-card shadow-xl p-3"
        >
          <p className="text-xs font-semibold text-ink-secondary mb-2">
            {pendingRating === 'up' ? 'Marking as helpful' : 'Marking as not helpful'}
          </p>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Why? (optional)"
            rows={2}
            autoFocus
            className="w-full text-xs border border-hairline rounded-inner px-2 py-1.5 resize-none focus:outline-none focus:border-signature mb-2"
          />
          {submitError && (
            <p className="text-xs text-error mb-2">{submitError}</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="tertiary" size="sm" onClick={closePopover}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : 'Submit'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default EntryFeedbackControl;
